import i18n from '../i18n';
import type { UserRole } from '../stores/auth.store';

/**
 * Sprint 28c — RBAC v2 rol metadata.
 *
 * UI'da dropdown/label/badge/açıklama için tek doğruluk kaynağı.
 *
 * i18n: `label`/`shortLabel`/`description` artık modül scope'unda TR literal
 * tutmaz; erişim anında `enum` namespace'inden çözülür (runtime TR/EN toggle).
 * Bu plain util React component olmadığından `useTranslation` yerine doğrudan
 * `i18n.t` instance'ı kullanılır (react-i18next non-component pattern).
 */

export interface RoleMeta {
  value: UserRole;
  label: string;
  shortLabel: string;
  description: string;
  /** Görsel grup — UI'da kategoriye ayırma için */
  group: 'executive' | 'finance' | 'management' | 'staff';
}

/** Yapısal rol tanımı — text alanları getter ile i18n'den çözülür. */
const ROLE_DEFS: { value: UserRole; group: RoleMeta['group'] }[] = [
  { value: 'OWNER', group: 'executive' },
  { value: 'CEO', group: 'executive' },
  { value: 'CFO', group: 'finance' },
  { value: 'ACCOUNTANT', group: 'finance' },
  { value: 'DEPARTMENT_MANAGER', group: 'management' },
  { value: 'TEAM_LEAD', group: 'management' },
  { value: 'EMPLOYEE', group: 'staff' },
  { value: 'EXPENSE_ONLY', group: 'staff' },
];

export const ROLES: RoleMeta[] = ROLE_DEFS.map(({ value, group }) => ({
  value,
  group,
  get label() {
    return i18n.t('role.' + value, { ns: 'enum' });
  },
  get shortLabel() {
    return i18n.t('roleShort.' + value, { ns: 'enum' });
  },
  get description() {
    return i18n.t('roleDesc.' + value, { ns: 'enum' });
  },
}));

export const ROLE_BY_VALUE: Record<UserRole, RoleMeta> = Object.fromEntries(ROLES.map((r) => [r.value, r])) as Record<
  UserRole,
  RoleMeta
>;

export function roleLabel(role: UserRole | null | undefined): string {
  if (!role) return '—';
  return ROLE_BY_VALUE[role]?.shortLabel ?? role;
}

/**
 * Tam Türkçe rol etiketi (örn. "Muhasebeci", "Şirket Sahibi"). Header/profil gibi
 * yer kısıtı olmayan, kısaltma yerine tam okunur etiket istenen yerlerde kullanılır.
 * `shortLabel` aksine İngilizce kısaltma (Owner/CEO/CFO) içermez.
 */
export function roleFullLabel(role: UserRole | null | undefined): string {
  if (!role) return '—';
  return ROLE_BY_VALUE[role]?.label ?? role;
}

export function isManagerRole(role: UserRole | null | undefined): boolean {
  if (!role) return false;
  return ['OWNER', 'CEO', 'CFO', 'DEPARTMENT_MANAGER', 'TEAM_LEAD'].includes(role);
}

export function isExecutiveRole(role: UserRole | null | undefined): boolean {
  if (!role) return false;
  return ['OWNER', 'CEO', 'CFO'].includes(role);
}

/**
 * Self-only roller — yalnızca kendi datasını gören çalışan rolleri (backend
 * `isSelfOnlyRole` ile birebir). Ekip/çalışan/org gibi yönetici-only ekranlardan
 * route + menü düzeyinde hariç tutmak için kullanılır (R1).
 */
export const SELF_ONLY_ROLES: UserRole[] = ['EMPLOYEE', 'EXPENSE_ONLY'];

export function isSelfOnlyRole(role: UserRole | null | undefined): boolean {
  return !!role && SELF_ONLY_ROLES.includes(role);
}

/**
 * KYB firma kaydını tamamlayabilecek roller — `/firma-kaydi` sayfası bu allow-list
 * ile role-locked (bkz. App.tsx `<RequireRole roles={['OWNER','CEO','CFO']}>`).
 *
 * PENDING_KYB feature-lock'un tek doğruluk kaynağı: RequireActiveCompany, OverviewPage
 * ve PendingKybBanner bu listeyi paylaşır ki "yönlendir vs. bilgilendir" ve "CTA göster
 * vs. gizle" kararları `/firma-kaydi` route-lock'u ile birebir tutarlı kalsın.
 * Bu roller dışındaki kullanıcılar sayfaya erişemez → redirect döngüsü/dead-link olmamalı.
 */
export const KYB_REGISTRATION_ROLES: UserRole[] = ['OWNER', 'CEO', 'CFO'];

export function canCompleteCompanyRegistration(role: UserRole | null | undefined): boolean {
  return !!role && KYB_REGISTRATION_ROLES.includes(role);
}
